import React, { useState, useEffect } from 'react';
import {
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Snackbar,
    Tooltip,
    Divider,
    Box,
    CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { apiCall } from '../../services/ApiCall';
import config from '../../config/config.json';
import { sweetAlert } from '../../utils/UniversalFunction';

const CopyUrlsModal = ({ open, item, onClose }) => {
    const [snackbarText, setSnackbarText] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [generatingLinks, setGeneratingLinks] = useState(false);
    const [links, setLinks] = useState({
        mainContactLoginUrl: item?.mainContactLoginUrl,
        siteAdminLoginUrl: item?.siteAdminLoginUrl,
        siteManagerLoginUrl: item?.siteManagerLoginUrl
    });

    useEffect(() => {
        if (open && item) {
            setLinks({
                mainContactLoginUrl: item.mainContactLoginUrl,
                siteAdminLoginUrl: item.siteAdminLoginUrl,
                siteManagerLoginUrl: item.siteManagerLoginUrl
            });
        }
    }, [open, item]);


    const handleCopy = async (text, label, roleType) => {
        // If link doesn't exist, generate it first when user clicks
        if (!text && item?.id) {
            try {
                setGeneratingLinks(true);
                // Generate and get the specific link
                const { isSuccess, data } = await apiCall(
                    `${config.POST_LOGIN_API_URL}getLoginLinks`,
                    {},
                    { requestId: item.id, roleType },
                    'GET'
                );
                
                if (isSuccess && data?.data) {
                    const linkToCopy = roleType === 'mainContact' ? data.data.mainContactLoginUrl :
                                     roleType === 'siteAdmin' ? data.data.siteAdminLoginUrl :
                                     data.data.siteManagerLoginUrl;
                    
                    if (linkToCopy) {
                        navigator.clipboard.writeText(linkToCopy);
                        setSnackbarText(`${label} copied!`);
                        setSnackbarOpen(true);
                        // Update local state
                        setLinks(prev => ({ ...prev, [roleType === 'mainContact' ? 'mainContactLoginUrl' : 
                                                       roleType === 'siteAdmin' ? 'siteAdminLoginUrl' : 
                                                       'siteManagerLoginUrl']: linkToCopy }));
                    } else {
                        sweetAlert('error', 'Link could not be generated. Please ensure the contact is assigned.');
                    }
                }
            } catch (error) {
                console.error('Error generating link:', error);
                sweetAlert('error', 'Failed to generate link');
            } finally {
                setGeneratingLinks(false);
            }
        } else if (text) {
            navigator.clipboard.writeText(text);
            setSnackbarText(`${label} copied!`);
            setSnackbarOpen(true);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
                <DialogTitle>Copy Login URLs</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography>Main Contact URL</Typography>

                            {links?.mainContactLoginUrl
                                ? (
                                    <Tooltip title="Copy Main Contact Login URL">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(links.mainContactLoginUrl, 'Main URL', 'mainContact')}
                                            disabled={generatingLinks}
                                        >
                                            <ContentCopyIcon color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                )
                                : (
                                    <Tooltip title="Generate and copy link">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(null, 'Main URL', 'mainContact')}
                                            disabled={generatingLinks}
                                        >
                                            {generatingLinks ? <CircularProgress size={16} /> : <ContentCopyIcon color="primary" />}
                                        </IconButton>
                                    </Tooltip>
                                )
                            }
                        </Stack>

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography>Site Admin URL</Typography>

                            {links?.siteAdminLoginUrl
                                ? (
                                    <Tooltip title="Copy Site Admin Login URL">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(links.siteAdminLoginUrl, 'Admin URL', 'siteAdmin')}
                                            disabled={generatingLinks}
                                        >
                                            <ContentCopyIcon color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                )
                                : (
                                    <Tooltip title="Generate and copy link">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(null, 'Admin URL', 'siteAdmin')}
                                            disabled={generatingLinks}
                                        >
                                            {generatingLinks ? <CircularProgress size={16} /> : <ContentCopyIcon color="primary" />}
                                        </IconButton>
                                    </Tooltip>
                                )
                            }
                        </Stack>

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography>Site Manager URL</Typography>

                            {links?.siteManagerLoginUrl
                                ? (
                                    <Tooltip title="Copy Site Manager Login URL">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(links.siteManagerLoginUrl, 'Manager URL', 'siteManager')}
                                            disabled={generatingLinks}
                                        >
                                            <ContentCopyIcon color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                )
                                : (
                                    <Tooltip title="Generate and copy link">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(null, 'Manager URL', 'siteManager')}
                                            disabled={generatingLinks}
                                        >
                                            {generatingLinks ? <CircularProgress size={16} /> : <ContentCopyIcon color="primary" />}
                                        </IconButton>
                                    </Tooltip>
                                )
                            }
                        </Stack>

                        <Divider />


                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {item?.isDataFormComplete===1
                                    ? <CheckCircleIcon color="success" />
                                    : <CancelIcon color="error" />}
                                <Typography>
                                    Data Form {item?.isDataFormComplete===1 ? 'Completed' : 'Incomplete'}
                                </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                                {item?.isPictureFormComplete===1
                                    ? <CheckCircleIcon color="success" />
                                    : <CancelIcon color="error" />}
                                <Typography>
                                    Picture Form {item?.isPictureFormComplete ===1 ? 'Completed' : 'Incomplete'}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="outlined">Close</Button>
                </DialogActions>
            </Dialog>


            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarText}
            />
        </>
    );
};
export default CopyUrlsModal;
